// JD Analyzer hook for managing job description analysis state

import { useState, useCallback } from 'react';
import { analyzeJobDescription, AnalyzeJdResponse } from '../lib/api';

export interface UseJDAnalyzerReturn {
  result: AnalyzeJdResponse | null;
  isLoading: boolean;
  error: string | null;
  analyze: (jobDescription: string) => Promise<void>;
  clearResult: () => void;
}

// Example JDs for demo
export const STRONG_FIT_EXAMPLE = `Senior Quality Assurance Engineer

We're looking for an experienced QA Engineer to join our team. You'll work closely with our engineering team to ensure product quality.

Requirements:
- 3+ years of QA experience
- Experience with automated testing frameworks
- Strong understanding of software development lifecycle
- Experience with AWS services
- Background in NLP or AI/ML testing is a plus
- Excellent communication skills

Nice to have:
- Experience with Python or TypeScript
- Knowledge of CI/CD pipelines
- Experience in fast-paced startup environments`;

export const WEAK_FIT_EXAMPLE = `iOS Mobile Developer

We're seeking a Senior iOS Developer to build our next-generation mobile application.

Requirements:
- 5+ years of iOS development experience
- Expert-level Swift and Objective-C
- Published apps on the App Store
- Experience with Core Data, Core Animation
- Strong understanding of Apple Human Interface Guidelines
- Experience with mobile CI/CD (Fastlane, Bitrise)

Nice to have:
- Experience with SwiftUI
- ARKit/Reality Kit experience
- Machine learning integration (Core ML)`;

export function useJDAnalyzer(): UseJDAnalyzerReturn {
  const [result, setResult] = useState<AnalyzeJdResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyze = useCallback(async (jobDescription: string) => {
    if (!jobDescription.trim() || isLoading) return;

    setError(null);
    setIsLoading(true);
    setResult(null);

    try {
      const response = await analyzeJobDescription({
        jobDescription: jobDescription.trim(),
      });

      setResult(response);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze job description';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return {
    result,
    isLoading,
    error,
    analyze,
    clearResult,
  };
}
